import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

const slides = [
  {
    eyebrow: "ВАШЕ ВРЕМЯ — ВАШ МАРШРУТ",
    title: "Фокус становится путешествием",
    body: "Выберите, сколько хотите быть в фокусе. Waypoint предложит реальный маршрут на это время.",
  },
  {
    eyebrow: "ОДИН РЕЙС — ОДИН РЕЗУЛЬТАТ",
    title: "Оставьте шум на земле",
    body: "Запишите, что должно быть готово к посадке. Во время полёта останутся только маршрут и ваше дело.",
  },
  {
    eyebrow: "ТОЧКА ОТПРАВЛЕНИЯ",
    title: "Откуда начнём?",
    body: "Выберите домашний аэропорт. Его всегда можно будет изменить в профиле.",
  },
] as const;

const airports = [
  { code: "SVO", city: "Москва", name: "Шереметьево" },
  { code: "DME", city: "Москва", name: "Домодедово" },
  { code: "VKO", city: "Москва", name: "Внуково" },
] as const;

function prepareTelegram(requestFullscreen = false) {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return;
  webApp.ready();
  webApp.expand();
  webApp.setHeaderColor("#6fb4ef");
  webApp.setBackgroundColor("#f7f9fc");
  webApp.setBottomBarColor?.("#f7f9fc");
  if (requestFullscreen && webApp.initData) {
    try {
      webApp.requestFullscreen?.();
    } catch {
      // Older Telegram clients still receive expand() above.
    }
  }
}

function PlaneMark() {
  return (
    <svg viewBox="0 0 36 36" aria-hidden="true">
      <path d="M31.6 7.2c-1.1-1.1-3.2-.7-4.5.2l-5.2 3.7L10 7.3 7.5 9.1l8.7 6-5.1 3.7-4.5-1.2-2 1.4 4.7 3.4 1.9 5.5 2-1.4-.1-4.7 5.1-3.7 2.5 10.3 2.5-1.8.3-12.5 5.2-3.8c1.3-.9 2.4-2.8 1.3-3.9Z" fill="currentColor" />
    </svg>
  );
}

function SkyScene({ active }: { active: boolean }) {
  const reduceMotion = useReducedMotion();
  return (
    <div className="sky-scene" aria-hidden="true">
      <motion.div
        className="sun-glow"
        animate={reduceMotion ? undefined : { scale: [1, 1.08, 1], opacity: [0.65, 0.85, 0.65] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div className="cloud cloud-a" animate={reduceMotion ? undefined : { x: [0, 18, 0] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="cloud cloud-b" animate={reduceMotion ? undefined : { x: [0, -24, 0] }} transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }} />
      <svg className="route-line" viewBox="0 0 390 290" preserveAspectRatio="none">
        <motion.path
          d="M-18 230 C 76 220, 92 116, 191 145 S 299 95, 420 38"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="5 8"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: active ? 1 : 0.25, opacity: 0.72 }}
          transition={{ duration: reduceMotion ? 0.12 : 1.5, ease: [0.45, 0, 0.2, 1] }}
        />
      </svg>
      <motion.div
        className="flying-mark"
        initial={{ x: -100, y: 100, rotate: -14 }}
        animate={{ x: active ? 116 : -60, y: active ? 26 : 78, rotate: -18 }}
        transition={{ duration: reduceMotion ? 0.12 : 1.6, ease: [0.45, 0, 0.2, 1] }}
      >
        <PlaneMark />
      </motion.div>
    </div>
  );
}

function BoardingPass() {
  return (
    <motion.div className="boarding-pass" initial={{ y: 24, rotate: -2, opacity: 0 }} animate={{ y: 0, rotate: 1.5, opacity: 1 }} transition={{ duration: 0.55 }}>
      <div className="ticket-topline"><span>WAYPOINT</span><span>WP 025</span></div>
      <div className="ticket-route"><div><strong>SVO</strong><span>Москва</span></div><div className="ticket-flight"><PlaneMark /></div><div><strong>LED</strong><span>Петербург</span></div></div>
      <div className="ticket-divider" />
      <div className="ticket-task"><span>К ПОСАДКЕ</span><strong>Готов первый черновик</strong></div>
      <div className="ticket-meta"><span>25 MIN</span><span>SEAT 08A</span><span>684 KM</span></div>
    </motion.div>
  );
}

function AirportMap({ selected, onSelect }: { selected: string; onSelect: (code: string) => void }) {
  return (
    <div className="airport-map">
      <svg viewBox="0 0 360 230" aria-hidden="true">
        <path className="map-land" d="M-20 48C49 9 103 23 147 53c48 33 90 11 130 4 50-8 81 14 108 45v145H-20Z" />
        <path className="map-river" d="M-10 180c59-4 82-44 127-41 52 4 70 47 122 40 37-5 70-31 131-25" />
        <path className="map-road" d="M17 76c72 36 123 34 170 16 55-21 105-8 156 30" />
      </svg>
      {airports.map((airport, index) => (
        <button
          key={airport.code}
          className={`map-pin pin-${index + 1}${selected === airport.code ? " is-selected" : ""}`}
          onClick={() => onSelect(airport.code)}
          aria-label={`${airport.name}, ${airport.code}`}
          aria-pressed={selected === airport.code}
        >
          <span className="pin-dot" /><span className="pin-label">{airport.code}</span>
        </button>
      ))}
      <div className="map-location"><span /> Москва</div>
    </div>
  );
}

export function App() {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [airport, setAirport] = useState("SVO");
  const slide = slides[step];
  const selectedAirport = airports.find((item) => item.code === airport)!;

  useEffect(() => prepareTelegram(true), []);

  const next = () => {
    prepareTelegram(true);
    window.Telegram?.WebApp.HapticFeedback?.impactOccurred("light");
    if (step < slides.length - 1) {
      setDirection(1);
      setStep((current) => current + 1);
      return;
    }
    localStorage.setItem("waypoint:onboarding", JSON.stringify({ completed: true, homeAirport: airport }));
    window.Telegram?.WebApp.HapticFeedback?.notificationOccurred("success");
  };

  const back = () => {
    if (step === 0) return;
    setDirection(-1);
    setStep((current) => current - 1);
  };

  return (
    <main className="onboarding-shell">
      <header className="topbar">
        <button className={`back-button${step === 0 ? " is-hidden" : ""}`} onClick={back} aria-label="Назад">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <img className="brand-lockup" src="/brand/waypoint-wordmark.png" alt="Waypoint" />
        <button className="skip-button" onClick={() => { setDirection(1); setStep(2); }}>Пропустить</button>
      </header>

      <section className="visual-stage">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`visual-${step}`}
            className="visual-content"
            custom={direction}
            initial={{ opacity: 0, x: direction * 32, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: direction * -24, scale: 0.99 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && <SkyScene active />}
            {step === 1 && <BoardingPass />}
            {step === 2 && <AirportMap selected={airport} onSelect={setAirport} />}
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="content-sheet">
        <div className="progress" role="status" aria-label={`Шаг ${step + 1} из ${slides.length}`}>
          {slides.map((_, index) => <span key={index} className={index === step ? "is-active" : ""} />)}
        </div>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`copy-${step}`}
            custom={direction}
            initial={{ opacity: 0, x: direction * 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -16 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="copy"
          >
            <p className="eyebrow">{slide.eyebrow}</p>
            <h1>{slide.title}</h1>
            <p className="body-copy">{slide.body}</p>
            {step === 2 && <p className="airport-choice"><span>{selectedAirport.code}</span> {selectedAirport.name}</p>}
          </motion.div>
        </AnimatePresence>
        <button className="primary-button" onClick={next}>
          <span>{step === 0 ? "Начать путь" : step === 1 ? "Продолжить" : "Выбрать аэропорт"}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      </section>
    </main>
  );
}
